/**
 * @description
 * Invoice filters component for search and filter controls.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date 2025-01-06
 */

import { Search, Download } from 'lucide-react';
import type { InvoiceFiltersProps } from './types';

export function InvoiceFilters({
  searchTerm,
  statusFilter,
  typeFilter,
  clientFilter,
  clients,
  selectedInvoices,
  onSearchChange,
  onStatusFilterChange,
  onTypeFilterChange,
  onClientFilterChange,
  onBulkAction,
}: InvoiceFiltersProps) {
  return (
    <div className="rounded-lg border bg-card p-6">
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search invoices..."
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        {/* Status Filter */}
        <select
          value={statusFilter}
          onChange={(e) => onStatusFilterChange(e.target.value)}
          className="rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <option value="all">All Statuses</option>
          <option value="draft">Draft</option>
          <option value="open">Open</option>
          <option value="paid">Paid</option>
          <option value="void">Void</option>
          <option value="uncollectible">Uncollectible</option>
        </select>

        {/* Type Filter */}
        <select
          value={typeFilter}
          onChange={(e) => onTypeFilterChange(e.target.value)}
          className="rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <option value="all">All Types</option>
          <option value="usage">Usage</option>
          <option value="subscription">Subscription</option>
        </select>

        {/* Client Filter */}
        <select
          value={clientFilter}
          onChange={(e) => onClientFilterChange(e.target.value)}
          className="rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <option value="all">All Clients</option>
          {clients.map(client => (
            <option key={client.id} value={client.id}>{client.name}</option>
          ))}
        </select>

        {/* Bulk Actions */}
        <div className="flex gap-2">
          <button
            onClick={() => onBulkAction('download')}
            disabled={selectedInvoices.length === 0}
            className="rounded-md border border-input bg-background px-3 py-2 text-sm font-medium transition-colors hover:bg-accent disabled:opacity-50"
          >
            <Download className="h-4 w-4" />
          </button>
          <button
            onClick={() => onBulkAction('mark_paid')}
            disabled={selectedInvoices.length === 0}
            className="rounded-md border border-input bg-background px-3 py-2 text-sm font-medium transition-colors hover:bg-accent disabled:opacity-50"
          >
            Mark Paid
          </button>
        </div>
      </div>
    </div>
  );
}
